import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import { StatefulRouteService } from '@app-store/services/stateful-route.service';
import { EVENT_MANAGER_CONSTANTS } from '@core/events-manager/events-manager.constants';
import { EventsManagerService } from '@core/events-manager/events-manager.service';
import { ShowSavedSuccessToast } from '@core/toaster/toaster-decorators';
import { _isNil } from '@core/lodash/lodash';
import { TaskStatus } from '@gandalf/constants';
import { CancelTaskRequest } from '@gandalf/model/cancel-task-request';
import { CreateTaskRequest } from '@gandalf/model/create-task-request';
import { PagingAndSortingRequest } from '@gandalf/model/paging-and-sorting-request';
import { ReassignTaskRequest } from '@gandalf/model/reassign-task-request';
import { SearchTasksRequest } from '@gandalf/model/search-tasks-request';
import { SortingRequest } from '@gandalf/model/sorting-request';
import { TaskListResponse } from '@gandalf/model/task-list-response';
import { UpdateTaskRequest } from '@gandalf/model/update-task-request';
import { TaskGandalfService } from '@gandalf/services';
import { ModalManagerService } from 'majora';
import { EnumUtil, SortingService } from 'morgana';
import { NewEntityUniqueIdService } from '../../services/new-entity-unique-id-service/new-entity-unique-id.service';
import { CreateTaskModalComponent, CreateTaskModalData } from '../../tasks/modals/create-task-modal/create-task-modal.component';

@Injectable({
	providedIn: 'root',
})
export class TaskService {
	private taskGandalfService = inject(TaskGandalfService);
	private eventsManagerService = inject(EventsManagerService);
	private modalManagerService = inject(ModalManagerService);
	private router = inject(Router);
	private newEntityUniqueIdService = inject(NewEntityUniqueIdService);

	private myTasksUpdatedSubject = new Subject<void>();
	myTasksUpdated$ = this.myTasksUpdatedSubject.asObservable();

	private refreshSubject = new Subject<void>();
	refresh$ = this.refreshSubject.asObservable();

	getTaskById(taskId: number) {
		return this.taskGandalfService.getTaskById(taskId);
	}

	getTasksByPatientId(patientId: number) {
		return this.taskGandalfService.findByPatientId(patientId).pipe(
			map(tasks => SortingService.sortBy(tasks, ['createdOn', 'taskId'], ['desc', 'desc'])),
		);
	}

	findMyTasks(request: PagingAndSortingRequest) {
		return this.taskGandalfService.findMyTasks(request);
	}

	searchTasks(request: SearchTasksRequest) {
		return this.taskGandalfService.searchTasks(request);
	}

	@ShowSavedSuccessToast()
	createTask(request: CreateTaskRequest) {
		return this.taskGandalfService.createTask(request);
	}

	@ShowSavedSuccessToast()
	updateTask(request: UpdateTaskRequest) {
		return this.taskGandalfService.updateTask(request);
	}

	cancelTask(request: CancelTaskRequest) {
		return this.taskGandalfService.cancelTask(request);
	}

	completeTask(taskId: number) {
		return this.taskGandalfService.completeTask(taskId);
	}

	acceptTask(taskId: number) {
		return this.taskGandalfService.acceptTask(taskId);
	}

	unacceptTask(taskId: number) {
		return this.taskGandalfService.unacceptTask(taskId);
	}

	reassignTask(request: ReassignTaskRequest) {
		return this.taskGandalfService.reassignTask(request);
	}

	markTaskAsRead(taskId: number) {
		return this.taskGandalfService.markTaskAsRead(taskId);
	}

	isOverdueAndActive(task: TaskListResponse): boolean {
		if (_isNil(task.dueDate) || _isNil(task.status)) {
			return false;
		}
		const isActiveOrUnassigned = EnumUtil.equalsOneOf(task.status, TaskStatus.ACTIVE, TaskStatus.UNASSIGNED);
		const isOverdue = dayjs(task.dueDate).startOf('day').isBefore(dayjs().startOf('day'));
		return isActiveOrUnassigned && isOverdue;
	}

	buildSortsFromSortModel(sortModel: { colId: string, sort: string }[]): SortingRequest[] {
		const sorts = sortModel.map(sort =>
			Object.assign(new SortingRequest(), {
				fieldName: sort.colId,
				descending: sort.sort === 'desc',
			}),
		);
		if (!sorts.some(sort => sort.fieldName === 'taskId')) {
			sorts.push(Object.assign(new SortingRequest(), { fieldName: 'taskId', descending: true }));
		}
		return sorts;
	}

	subscribeToEvents() {
		this.eventsManagerService
			.subscribeObservable(EVENT_MANAGER_CONSTANTS.TASKS.OPEN_TASK)
			.subscribe((event) => this.handleOpenTask(event.argument));
		this.eventsManagerService
			.subscribeObservable(EVENT_MANAGER_CONSTANTS.TASKS.ADD_TASK)
			.subscribe(() => this.handleAddTask());
	}

	private handleOpenTask(payload: any) {
		const taskId = payload?.id;
		if (taskId != null) {
			this.navigateToTask(taskId);
		}
	}

	private handleAddTask() {
		void StatefulRouteService.routePastPersistedRoute(this.router, ['/tasks', 'create', this.newEntityUniqueIdService.nextId]);
	}

	async openCreateTaskModal(data: CreateTaskModalData) {
		const modalRef = this.modalManagerService.open(CreateTaskModalComponent, data, {
			size: 'lg',
			backdrop: 'static',
			animation: false,
			windowClass: 'rev-modern',
			backdropClass: 'rev-modern',
		});
		const result = await modalRef.result;
		if (result) {
			this.myTasksUpdatedSubject.next();
		}
		return result;
	}

	publishMyTasksUpdated() {
		this.myTasksUpdatedSubject.next();
	}

	publishRefresh() {
		this.refreshSubject.next();
	}

	navigateToTask(taskId: number) {
		// Defer navigation so the current call stack completes before Angular routing
		setTimeout(() => {
			void StatefulRouteService.routePastPersistedRoute(this.router, ['/tasks', taskId]);
		});
	}
}
